<template>
  <modal name="modal-trip-history2" class="trip-history" transition="pop-out" width="95%" height="95%" :focus-trap="true" :scrollable="false" :adaptive="true" :reset="true" @before-close="reset">

    <!-- ── Header ───────────────────────────────────────────────── -->
    <div class="trip-header flex items-center justify-between px-4 bg-white border-b border-secondary-100">
      <div class="flex items-center gap-3 min-w-0">
        <div class="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center shadow-sm flex-shrink-0">
          <BaseIcon icon="car" class="text-white text-sm" />
        </div>
        <div class="min-w-0">
          <div class="font-semibold text-secondary-800 text-xl leading-tight truncate">{{ alarm.product_name || $t('trip_history.title') }}</div>
          <div class="text-xs text-secondary-600 leading-tight font-medium">
            <span v-if="isLoading">{{ $t('trip_history.loading_trip') }}</span>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <transition name="fade">
          <div v-if="isLoading" class="flex items-center gap-1.5 text-xxs text-primary-500 font-semibold bg-primary-50 px-2.5 py-1 rounded-full">
            <LoadingSpinner :isLoading="isLoading" />
            <span>{{ $t('trip_history.loading') }}</span>
          </div>
        </transition>
        <button @click="hide()" class="w-8 h-8 flex items-center justify-center rounded-lg text-secondary-400 hover:text-red-500 hover:bg-red-50 transition-all">
          <BaseIcon icon="times" class="text-base" />
        </button>
      </div>
    </div>

    <!-- ── Body ─────────────────────────────────────────────────── -->
    <div class="trip-body flex flex-col lg:flex-row" style="height: 100%;">

      <!-- ── LEFT PANEL ────────────────────────────────────────── -->
      <div class="left-panel flex flex-col bg-secondary-50">

        <!-- ── Trip summary (detail view) ── -->
        <div v-if="selectedTrip" class="px-3 pt-3 pb-2.5 bg-white border-b border-secondary-100 flex-shrink-0">
          <div class="flex items-center gap-2 min-w-0 mb-1.5">
            <div class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ backgroundColor: selectedTrip.color }"></div>
            <div class="font-bold text-sm text-secondary-800 truncate">{{ selectedTrip.label }} {{ $t('trip_history.trip_summary') }}</div>
          </div>
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="inline-flex items-center gap-1 text-xxs font-semibold text-primary-700 bg-primary-50 border border-primary-100 px-2 py-1 rounded-lg">
              <BaseIcon icon="road" class="text-primary-400" />{{ formatDist(selectedTrip.totalDistance) }} km
            </span>
            <span class="inline-flex items-center gap-1 text-xxs font-semibold text-brown-600 bg-brown-50 border border-brown-100 px-2 py-1 rounded-lg">
              <BaseIcon icon="clock" class="text-brown-400" />{{ moment.duration(selectedTrip.totalDuration, 'seconds').humanize() }}
            </span>
            <span class="inline-flex items-center gap-1 text-xxs font-semibold px-2 py-1 rounded-lg border"
              style="color:#F29100; background:rgba(242,145,0,0.09); border-color:rgba(242,145,0,0.28);">
              <BaseIcon icon="map-marker-alt" style="color:#F29100;" />{{ selectedTrip.timeline.filter(t => t.type === 'waiting').length }} {{ $t('trip_history.stops') }}
            </span>
          </div>
        </div>

        <!-- ── Scrollable content area ── -->
        <div class="flex-1 overflow-y-auto min-h-0 trip-scroll-area" ref="rightSidebar">

          <!-- Loading skeletons -->
          <div v-if="isLoading" class="px-3 pt-3 pb-3 space-y-3">
            <div v-for="n in 3" :key="'sk-' + n" class="bg-white rounded-xl border border-secondary-100 p-3 animate-pulse">
              <div class="flex justify-between mb-3">
                <div><div class="h-3.5 bg-secondary-100 rounded w-28 mb-1.5"></div><div class="h-2.5 bg-secondary-100 rounded w-20"></div></div>
                <div class="text-right"><div class="h-3.5 bg-secondary-100 rounded w-14 mb-1.5"></div><div class="h-2.5 bg-secondary-100 rounded w-20"></div></div>
              </div>
              <div class="space-y-2 mb-3">
                <div class="flex items-center gap-2"><div class="w-1.5 h-1.5 rounded-full bg-secondary-100 flex-shrink-0"></div><div class="h-2.5 bg-secondary-100 rounded flex-1"></div></div>
                <div class="flex items-center gap-2"><div class="w-1.5 h-1.5 rounded-full bg-secondary-100 flex-shrink-0"></div><div class="h-2.5 bg-secondary-100 rounded w-3/4"></div></div>
              </div>
              <div class="flex gap-0.5 mb-3"><div v-for="j in 7" :key="j" class="h-1.5 bg-secondary-100 rounded-full flex-1"></div></div>
              <div class="h-8 bg-secondary-100 rounded-xl"></div>
            </div>
          </div>

          <!-- ── TRIP DETAIL VIEW (Timeline) ── -->
          <div v-if="hasFetchedData && selectedTrip && !isLoading" class="px-3 pt-2 pb-4">
            <div class="flex items-center justify-between py-2">
              <span class="text-sm font-bold text-primary-700 uppercase tracking-widest py-1">{{ $t('trip_history.timeline') }}</span>
              <div class="flex items-center gap-1 relative z-10">
                <tooltip :message="$t('trip_history.change_route_color')" position="left" :disabled="!selectedTrip?.timeline?.length">
                  <button class="w-6 h-6 rounded-md flex items-center justify-center text-xs transition-all"
                    :class="pathColorChanged ? 'bg-primary-100 text-primary-500' : 'text-secondary-500 hover:bg-secondary-100 hover:text-secondary-600'"
                    @click="ChangePathColor()"><BaseIcon icon="palette" /></button>
                </tooltip>
                <tooltip :message="$t('trip_history.copy_directions')" position="left">
                  <button class="w-6 h-6 rounded-md flex items-center justify-center text-xs transition-all text-secondary-500 hover:bg-secondary-100 hover:text-secondary-600"
                    @click="copyDirectionLink"><BaseIcon icon="directions" /></button>
                </tooltip>
              </div>
            </div>
            <!-- ── Journey Flow Timeline ─────────────────────────── -->
        
            <div class="journey-flow pr-1" :style="{ '--line-bottom': lineBottom }">
              <template v-for="(node, ni) in timelineNodes">

                <!-- ─ WAYPOINT NODE (matching RoutePreviewTimeline structure) ─ -->
                <div :key="'jn-' + ni"
                  :id="node.routeIndex !== undefined ? 'jf-node-' + node.routeIndex : undefined"
                  class="route-waypoint-main">

                  <!-- Icon circle (absolute positioned) -->
                  <div class="route-icon-circle"
                    :class="{
                      'route-icon-start': node.nodeType === 'origin' && completedNodeIndices[node.routeIndex],
                      'route-icon-end': node.nodeType === 'final' && completedNodeIndices[node.routeIndex],
                      'route-icon-stop': node.nodeType === 'stop' && completedNodeIndices[node.routeIndex],
                      'route-icon-active': isPlaying && activeNodeRouteIndex === node.routeIndex,
                      'route-icon-pending': !completedNodeIndices[node.routeIndex] && !(isPlaying && activeNodeRouteIndex === node.routeIndex)
                    }">
                    <BaseIcon icon="map-marker" class="text-white" style="font-size:12px;" />
                  </div>

                  <!-- Card with left border -->
                  <div class="route-point"
                    :class="{
                      'bg-blue-50': node.nodeType === 'origin' || node.nodeType === 'stop',
                      'bg-amber-50': node.nodeType === 'final',
                      'active-playing': isPlaying && activeNodeRouteIndex === node.routeIndex
                    }">
                    <div class="flex-1 min-w-0">
                      <!-- Label -->
                      <div class="text-xxs font-semibold uppercase mb-1"
                        :class="{
                          'text-blue-600': node.nodeType === 'origin' || node.nodeType === 'stop',
                          'text-amber-600': node.nodeType === 'final'
                        }">
                        {{ node.nodeType === 'origin' ? $t('trip_planner.trip_start_label') || 'Trip Start'
                          : node.nodeType === 'final' ? $t('trip_planner.final_destination_label') || 'Final Destination'
                          : ($t('trip_planner.stop_label') || 'Stop') + ' ' + node.order }}
                      </div>

                      <!-- Address -->
                      <div class="address text-gray-800">
                        {{ node.address || '—' }}
                      </div>

                      <!-- Trip leg info (stops + destination) -->
                      <div v-if="(node.nodeType === 'stop' || node.nodeType === 'final') && node.legInfo" class="text-xs text-gray-600 font-semibold mt-1">
                        {{ node.legInfo.distance?.text }}, {{ $te('trip_planner.about') ? $t('trip_planner.about') : 'About' }} {{ node.legInfo.duration?.text }}
                      </div>

                      <!-- Timestamp -->
                      <div class="text-xxs tabular-nums text-gray-500 mt-1">
                        <template v-if="node.nodeType === 'stop'">
                          {{ moment(node.startTime).format('D MMM YYYY, HH:mm') }} – {{ moment(node.endTime).format('HH:mm') }}
                        </template>
                        <template v-else>
                          {{ moment(node.time).format('D MMM YYYY, HH:mm') }}
                        </template>
                      </div>

                      <!-- Focus button -->
                      <button
                        @click.stop="focusWaypointNode(node)"
                        class="absolute top-3 right-3 w-7 h-7 flex items-center cursor-pointer justify-center rounded-lg
                               text-secondary-400 hover:text-primary-600 hover:bg-primary-50 transition-all">
                        <BaseIcon icon="crosshairs" style="font-size:11px;" />
                      </button>
                    </div>
                  </div>
                </div>

              </template>
            </div>
          </div>

        </div>

        <!-- ── Playback Controls (detail view only) ── -->
        <div v-if="selectedTrip" class="px-3 py-3 bg-white border-t border-secondary-100 flex-shrink-0">
          <div class="flex items-center gap-2 mb-2">
            <button
              @click="playAnimation()"
              class="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl font-bold text-xs transition-all duration-200 active:scale-95"
              :class="isPlaying && !isPaused ? 'bg-orange-100 text-orange-600 border border-orange-200 hover:bg-orange-200' : 'bg-primary-500 text-white hover:bg-primary-600 shadow-sm'">
              <BaseIcon :icon="isPlaying && !isPaused ? 'pause' : 'play'" />
              {{ isPlaying && isPaused ? $t('trip_history.resume') : isPlaying ? $t('trip_history.pause') : $t('trip_history.play') }}
            </button>
            <button
              @click="stopAnimation()"
              class="w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 active:scale-95 border"
              :class="isPlaying || stopPlay ? 'bg-red-50 text-red-500 border-red-100 hover:bg-red-100' : 'bg-secondary-100 text-secondary-400 border-secondary-100 hover:bg-secondary-200'">
              <BaseIcon icon="stop" />
            </button>
          </div>
          <div class="flex items-center gap-2">
            <BaseIcon icon="tachometer-alt" class="text-secondary-300 text-xs flex-shrink-0" />
            <input type="range" min="0" max="500" step="10" v-model.number="playSpeed" class="flex-1 speed-slider" />
            <span class="text-xxs font-bold tabular-nums w-8 text-right" style="color:#1A8DE9;">{{ Math.round(playSpeed / 5) }}×</span>
          </div>
        </div>
      </div>

      <!-- ── RIGHT PANEL / MAP ──────────────────────────────────── -->
      <div class="flex-1 map-panel relative">

        <!-- Trip not found overlay -->
        <transition name="fade">
          <div v-if="hasFetchedData && !selectedTrip && !isLoading" class="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div class="bg-white rounded-2xl shadow-lg border border-secondary-100 px-6 py-5 px-8 py-7 text-center max-w-sm mx-4">
              <div class="w-14 h-14 rounded-2xl bg-secondary-50 border border-secondary-100 flex items-center justify-center mx-auto mb-4">
                <BaseIcon icon="route" class="text-secondary-500 text-2xl" />
              </div>
              <p class="text-base font-bold text-secondary-700 mb-1">{{ $t('trip_history.trip_not_found') }}</p>
              <p class="text-xs text-secondary-400 leading-relaxed">{{ $t('trip_history.trip_not_found_desc') }}</p>
            </div>
          </div>
        </transition>

        <!-- Live info floating card -->
        <transition name="info-slide">
          <div class="live-info-card" v-if="isPlaying && stickyInfoContent">
            <div v-html="stickyInfoContent" class="live-info-text"></div>
          </div>
        </transition>

        <!-- Map -->
        <GmapMap
          :center="center"
          :zoom="zoom"
          map-type-id="terrain"
          class="w-full h-full"
          ref="tripHistoryMap"
          @click="onMapClick">
          <GmapInfoWindow :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="closeInfoWindow" />
          <GmapCircle v-for="(pin, index) in simulatedPoints" :key="index + 'c'" :center="pin.position" :visible="true" :options="pin.options" @mouseover="showInfoWindow($event, pin.infoText, true)" @mouseout="showInfoWindow($event, pin.infoText, false)" />
          <GmapMarker :position="marker.position" :clickable="false" :draggable="false" :icon="marker.icon" />

          <GmapMarker v-for="(m, index) in simulatedPoints" :key="index" :position="m.position" :clickable="true" :draggable="false" :label="String(m.order + 1)" @mouseover="showInfoWindow($event, m.infoText, true)" @mouseout="showInfoWindow($event, m.infoText, false)" />
          <template v-if="isPlaying === true">
            <GmapPolyline v-for="(p, i) in simulatedPaths" :key="i + 'p'" :path="p.path" :options="p.options" />
          </template>
         


          <template v-if="trackedRoute.length > 0">
            <GmapMarker v-for="(dm, di) in destinationMarkers" :key="`DM-${di}`" :position="dm.position" :clickable="true" :draggable="false"
              :label="String(di + 1)"
              @mouseover="showRouteInfoWindow($event, dm.infoText, true)" @mouseout="showRouteInfoWindow($event, dm.infoText, false)" />

            <template v-if="isPlaying === false">
              <!-- Segment polylines for hover and click detection -->
              <GmapPolyline 
                v-for="(point, index) in trackedRoute" 
                :key="'segment-polyline-' + index" 
                :path="trackedRoute.slice(index, index + 2)"
                :clickable="true"
                :options="{ 
                  strokeColor: '#1A8DE9',
                  strokeOpacity: 1,
                  strokeWeight: 5,
                }"
                @mouseover="showTrackedPointInfo(point, index)"
                @mouseout="closeTrackedPointInfoUnlessPinned"
                @click="pinTrackedPointInfo(point, index)"
              ></GmapPolyline>
            </template>
          </template>

          <!-- ── Crash Detected Markers ───────────────────────── -->
          <GmapMarker
            v-for="(crash, ci) in displayedCrashMarkers"
            :key="'crash-' + ci"
            :position="crash.position"
            :clickable="true"
            :draggable="false"
            :icon="getCrashMarkerIcon()"
            :z-index="99"
            @mouseover="showCrashInfoWindow(crash)"
            @mouseout="hideCrashInfoWindow"
            @click="pinCrashInfoWindow(crash)"
          />
        </GmapMap>
      </div>

    </div>
  </modal>
</template>

<script>
import { gmapApi } from "vue2-google-maps";
import tooltip from "@/components/base/tooltip";
import moment from 'moment-timezone';

export default {
  name: "TripTracker",

  components: {
    tooltip,
  },

  props: {},

  data() {
    return {
      data: null,
      zoom: this.$store.state.mapZoom,
      center: this.$store.state.mapCenter,
      isLoading: false,
      startPosition: null,
      endPosition: null,
      departureTime: null,
      waypoints: [],
      infoWindowPos: null,
      infoWinOpen: false,
      infoWinLocked: false,
      infoOptions: { content: "", icon: "", pixelOffset: { width: 0, height: -35 } },
      isPlaying: false,
      isPaused: false,
      stopPlay: false,
      playSpeed: 400,
      currentPlayingIndex: -1,
      totalDist: 0,
      totalTripDuration: 0,
      simulatedPoints: [],
      marker: {},
      simulatedPaths: [],
      trackedRoute: [],
      pinnedTrackedPointInfo: false,
      isCrashHovered: false,
      isCrashPinned: false,
      pathColor: { regularColor: "red", changedColor: "#1A8DE9", activeColor: "red" },
      pathColorChanged: false,
      alarm: { unit_name: "", product_name: "" },
      mapKey: Date.now(),
      mapStartPosition: null, //for map bund
      mapEndPosition: null, //for map bund
      stickyInfoContent: null,
      tripId: null,
      selectedTrip: null,
      hasFetchedData: false,
      completedNodeIndices: {},
      revealedMarkerCount: null, // null = show all; number = show first N during simulation
      // Securdia brand color rotation for timeline bars
      brandPalette: ['#1A8DE9', '#1C497C', '#00C16E', '#F29100', '#A16D46'],
      // Crash detected events extracted from trip_log
      crashMarkers: [],
      simulatedCrashMarkers: [], // progressively revealed during simulation
    };
  },
  computed: {
    google: gmapApi,
    actualDelay() {
      const maxSpeed = 500;
      const exponent = 3;
      const minDelay = 10;
      const maxDelay = 500;
      const speedRatio = this.playSpeed / maxSpeed;
      const delay = maxDelay * Math.pow(1 - speedRatio, exponent);
      return Math.max(minDelay, delay);
    },
    // During simulation show only progressively revealed crash markers;
    // when idle show all of them.
    displayedCrashMarkers() {
      return this.isPlaying ? this.simulatedCrashMarkers : this.crashMarkers;
    },
    // ── Journey Flow: flat ordered list of waypoint nodes ───────
    //
    // Transforms routeResults into a chain suitable for the station-map timeline:
    //
    //   { nodeType: 'origin'      }  ← derived from trip[0].startAddress
    //   { nodeType: 'stop',  ...waitingItem, routeIndex }  ← 0-N intermediate stops
    //   ...
    //   { nodeType: 'final'       }  ← derived from lastTrip.endAddress
    //
    timelineNodes() {
      const timeline = this.selectedTrip?.timeline || [];
      if (!timeline.length) return [];

      const nodes = [];
      const tripSegments = timeline.filter(r => r.type === 'trip');

      // Origin comes from the first trip's startAddress
      const firstTrip = tripSegments[0];
      if (!firstTrip) return nodes;
      
      nodes.push({
        nodeType:  'origin',
        routeIndex: timeline.indexOf(firstTrip),
        address:   firstTrip.startAddress || '—',
        time:      firstTrip.startTime,
        position:  firstTrip.startPosition,
        infoText:  `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:190px;padding:6px 4px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
            <span style="background:#1A8DE9;color:white;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_planner.trip_start_label') || 'Trip Start'}</span>
          </div>
          <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${firstTrip.startAddress || '—'}</div>
          <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(firstTrip.startTime).format('D MMM, HH:mm')}</span>
        </div>`,
      });

      // Walk the flat timeline array, only add stop (waiting) nodes
      let stopOrder = 0;
      for (let i = 0; i < timeline.length; i++) {
        const item = timeline[i];
        if (item.type === 'waiting') {
          // For stop N, use tripSegments[N] as the ARRIVAL leg (prev -> this stop)
          const arrivalTrip = tripSegments[stopOrder];
          const node = { 
            nodeType: 'stop', 
            routeIndex: i, 
            ...item 
          };
          
          // Add leg info for the segment that reaches this stop
          if (arrivalTrip) {
            node.nextDestination = arrivalTrip.endAddress || '—';
            node.legInfo = {
              distance: { text: this.formatDist(arrivalTrip.distance) + ' km' },
              duration: { text: this.moment.duration(arrivalTrip.duration, 'seconds').humanize() }
            };
          }
          
          nodes.push(node);
          stopOrder++;
        }
      }

      // Final destination comes from the last trip's endAddress
      const lastTrip = tripSegments[tripSegments.length - 1];
      if (lastTrip) {
        nodes.push({
          nodeType:  'final',
          routeIndex: timeline.lastIndexOf(lastTrip),
          address:   lastTrip.endAddress || '—',
          time:      lastTrip.endTime,
          position:  lastTrip.endPosition,
          legInfo: {
            distance: { text: this.formatDist(lastTrip.distance) + ' km' },
            duration: { text: this.moment.duration(lastTrip.duration, 'seconds').humanize() }
          },
          infoText:  `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:180px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
              <span style="background:#1A8DE9;color:white;font-size:10px;font-weight:700;padding:2px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_planner.final_destination_label') || 'Final Destination'}</span>
            </div>
            <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:6px;line-height:1.4;">${lastTrip.endAddress || '—'}</div>
            <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:2px 8px;border-radius:6px;">${this.moment(lastTrip.endTime).format('D MMM, HH:mm')}</span>
          </div>`,
        });
      }

      return nodes;
    },

    // ── Flat list of all destination markers (origin → stops → final) ──
    destinationMarkers() {
      const markers = [];
      const timeline = this.selectedTrip?.timeline || [];
      if (!timeline.length) return markers;

      const trips = timeline.filter(r => r.type === 'trip');
      const first = trips[0];
      const last  = trips[trips.length - 1];

      // 1. Origin – start of first trip (fall back to first path point if startPosition is null)
      const originPos = first?.startPosition
        || (first?.path?.length ? { lat: first.path[0].lat, lng: first.path[0].lng } : null);
      if (originPos) {
        const originInfo = `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:190px;padding:6px 4px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
            <span style="background:#1A8DE9;color:white;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_planner.trip_start_label') || 'Trip Start'}</span>
          </div>
          <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${first?.startAddress || '—'}</div>
          <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${first?.startTime ? this.moment(first.startTime).format('D MMM, HH:mm') : '—'}</span>
        </div>`;
        markers.push({ position: originPos, infoText: originInfo });
      }

      // 2. Intermediate stops (waiting segments)
      for (const r of timeline) {
        if (r.type === 'waiting' && r.position) {
          markers.push({ position: r.position, infoText: r.infoText || '' });
        }
      }

      // 3. Final destination – end of last trip (fall back to last path point if endPosition is null)
      const finalPos = last?.endPosition
        || (last?.path?.length ? { lat: last.path[last.path.length - 1].lat, lng: last.path[last.path.length - 1].lng } : null);
      if (finalPos) {
        const finalInfo = `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:190px;padding:6px 4px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
            <span style="background:#1A8DE9;color:white;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_planner.final_destination_label') || 'Final Destination'}</span>
          </div>
          <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${last?.endAddress || '—'}</div>
          <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${last?.endTime ? this.moment(last.endTime).format('D MMM, HH:mm') : '—'}</span>
        </div>`;
        markers.push({ position: finalPos, infoText: finalInfo });
      }

      // During simulation, only show the origin marker — stops are rendered
      // via simulatedPoints (with circles), final appears when simulation ends
      if (this.revealedMarkerCount !== null) {
        return markers.slice(0, 1);
      }
      return markers;
    },

    // Keep same simple behavior as RoutePreviewTimeline
    lineBottom() {
      const stopsCount = this.timelineNodes.filter(n => n.nodeType === 'stop').length;
      if (stopsCount === 0) {
        return '50px';
      }
      // Dynamic: first stop aligns around ~95px, then add a small offset per extra stop
      const baseBottom = 100;
      const perExtraStop = 4;
      return `${baseBottom + Math.max(0, stopsCount - 1) * perExtraStop}px`;
    },


    // ── Which timeline node is currently active ──────────────
    // Finds the last node whose routeIndex ≤ currentPlayingIndex.
    // One node is always active; it stays lit during transit legs
    // until the next waypoint is reached.
    activeNodeRouteIndex() {
      if (!this.isPlaying || this.currentPlayingIndex < 0) return -1;
      let active = -1;
      for (const node of this.timelineNodes) {
        if (node.routeIndex <= this.currentPlayingIndex) {
          active = node.routeIndex;
        }
      }
      return active;
    },

    // ── New computed for new UI ─────────────────────────────
  },
  methods: {
    async getRouteResult() {
      this.selectedTrip = null;
      this.hasFetchedData = false;
      this.simulatedPoints = [];
      this.simulatedPaths = [];
      this.trackedRoute = [];
      this.isPlaying = false;
      this.isPaused = false;
      this.stopPlay = false;
      this.currentPlayingIndex = -1;
      this.stickyInfoContent = null;
      this.infoWinOpen = false;
      this.pathColorChanged = false;
      this.startPosition = null;
      this.endPosition = null;
      this.waypoints = [];
      this.mapKey = Date.now();
      this.isLoading = true;

      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/asset-tracker/trips/${this.tripId}`);
        this.data = response.data?.trip || response.data;

        if (this.data) {
          this.trackedRoute = this.data.tracked_route || [];
          const trip = this.transformTripData(this.data);
          this.hasFetchedData = true;
          this.isLoading = false;
          await this.selectTrip(trip);
          return;
        }
      } catch (error) {
        console.error('Error fetching trip data:', error);
      }

      this.isLoading = false;
      this.hasFetchedData = true;
    },
    setMapBounds() {
      var vm = this;

      this.$nextTick(() => {
        vm.$refs.tripHistoryMap.$mapPromise.then((map) => {
          const bounds = new vm.google.maps.LatLngBounds();

          if (vm.mapStartPosition) bounds.extend(vm.mapStartPosition);
          if (vm.mapEndPosition) bounds.extend(vm.mapEndPosition);

          map.fitBounds(bounds);
          vm.zoom = 12;
        });
      });
    },

    // Fill startAddress / endAddress / address on timeline segments that are still empty.
    // Only runs reverse-geocoding when the value is missing – safe to call for demo data too.
    async geocodeTimelineAddresses() {
      const tl = this.selectedTrip.timeline;
      for (let i = 0; i < tl.length; i++) {
        const seg = tl[i];
        if (seg.type === 'trip') {
          if (!seg.startAddress) {
            this.$set(tl, i, {
              ...seg,
              startAddress: await this.reverseGeocode(seg.startPosition),
            });
          }
          const updated = tl[i];
          if (!updated.endAddress) {
            this.$set(tl, i, {
              ...tl[i],
              endAddress: await this.reverseGeocode(updated.endPosition),
            });
          }
          // Set infoText for trips
          const finalSeg = tl[i];
          finalSeg.infoText = `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:200px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
              <span style="background:#1A8DE9;color:white;font-size:10px;font-weight:700;padding:2px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_history.trip_label').toUpperCase()} ${finalSeg.order}</span>
              <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:700;padding:1px 8px;border-radius:6px;">${this.formatDist(finalSeg.distance)} km</span>
            </div>
            <div style="font-size:12px;color:#1e293b;margin-bottom:8px;">
              <span style="color:#00C16E;font-weight:600;">${this.formatAdd(finalSeg.startAddress)}</span>
              <span style="color:#94a3b8;margin:0 5px;">→</span>
              <span style="color:#ef4444;font-weight:600;">${this.formatAdd(finalSeg.endAddress)}</span>
            </div>
            <div style="display:flex;gap:4px;">
              <div style="flex:1;text-align:center;background:#EBF5FF;border-radius:8px;padding:5px 3px;">
                <div style="font-size:12px;font-weight:800;color:#1A8DE9;">${this.moment(finalSeg.startTime).format('HH:mm')} → ${this.moment(finalSeg.endTime).format('HH:mm')}</div>
                <div style="font-size:9px;color:#1A8DE9;opacity:.7;text-transform:uppercase;letter-spacing:.05em;margin-top:1px;">${this.$t('trip_history.time_label')}</div>
              </div>
              <div style="flex:1;text-align:center;background:#FFF7ED;border-radius:8px;padding:5px 3px;">
                <div style="font-size:12px;font-weight:800;color:#F29100;">${this.moment.duration(finalSeg.duration, 'seconds').humanize()}</div>
                <div style="font-size:9px;color:#F29100;opacity:.7;text-transform:uppercase;letter-spacing:.05em;margin-top:1px;">${this.$t('trip_history.duration')}</div>
              </div>
            </div>
          </div>`;
        } else if (seg.type === 'waiting') {
          // Always resolve address from actual GPS position; fall back to waypoint address.
          const geocoded = seg.position ? await this.reverseGeocode(seg.position) : '';
          this.$set(tl, i, {
            ...seg,
            address: geocoded || seg.waypointAddress || seg.address,
          });
          // Always fetch the GPS-derived street address for the break position
          if (!seg.locationAddress && seg.position) {
            const locAddr = await this.reverseGeocode(seg.position);
            if (locAddr) {
              this.$set(tl, i, {
                ...tl[i],
                locationAddress: locAddr,
              });
            }
          }
          // Set infoText for waiting
          const finalSeg = tl[i];
          finalSeg.infoText = `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:190px;padding:6px 4px;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
              <span style="background:#F29100;color:white;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_history.stop_label').toUpperCase()} ${finalSeg.order}</span>
              <span style="background:#FFF7ED;color:#F29100;font-size:11px;font-weight:700;padding:2px 9px;border-radius:6px;">${this.moment.duration(finalSeg.duration, 'seconds').humanize()}</span>
            </div>
            <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${this.formatAdd(finalSeg.address)}</div>
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(finalSeg.startTime).format('HH:mm')}</span>
              <span style="color:#cbd5e1;font-size:12px;">→</span>
              <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(finalSeg.endTime).format('HH:mm')}</span>
            </div>
          </div>`;
        }
      }

      // Geocode crash marker positions
      for (let i = 0; i < this.crashMarkers.length; i++) {
        const crash = this.crashMarkers[i];
        if (!crash.address && crash.position) {
          const addr = await this.reverseGeocode(crash.position);
          if (addr) {
            this.$set(this.crashMarkers, i, { ...crash, address: addr });
          }
        }
      }
    },

    async reverseGeocode(point) {
      let address = "";

      if (!point || typeof point.lat === "undefined" || typeof point.lng === "undefined") {
        return address;
      }

      try {
        const geocoder = new this.google.maps.Geocoder();
        let res = await geocoder.geocode({ location: point });

        if (!res.results || res.results.length === 0) {
          return address;
        }

        // Plus Code pattern - matches formats like "Q9W3+6FR" anywhere in the string
        const plusCodePattern = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}/i;

        // Preferred address types in order of priority
        const preferredTypes = [
          "street_address",
          "premise",
          "subpremise",
          "route",
          "intersection",
          "neighborhood",
          "sublocality",
          "locality",
          "administrative_area_level_3",
          "administrative_area_level_2",
        ];

        // First, try to find an address with preferred types that doesn't have Plus Code
        for (let type of preferredTypes) {
          for (let result of res.results) {
            if (result.types && result.types.includes(type)) {
              if (!plusCodePattern.test(result.formatted_address)) {
                return result.formatted_address;
              }
            }
          }
        }

        // Second pass: find any result without Plus Code
        for (let result of res.results) {
          if (!plusCodePattern.test(result.formatted_address)) {
            return result.formatted_address;
          }
        }

        // Third pass: try to clean Plus Code from the beginning of address
        for (let result of res.results) {
          let cleaned = result.formatted_address.replace(/^[A-Z0-9]{4,8}\+[A-Z0-9]{2,4},?\s*/i, "").trim();
          if (cleaned && cleaned.length > 5) {
            return cleaned;
          }
        }

        // Final fallback: return first result as-is
        address = res.results[0].formatted_address;
      } catch (error) {
        console.log("Reverse geocode error:", error);
      }

      return address;
    },

    processRoutePlayedItems() {
      this.routePlayedItems = [];
      const timeline = this.selectedTrip?.timeline || [];
      for (let i = 0; i < timeline.length; i++) {
        this.routePlayedItems.push(timeline[i]);
      }
    },
    async playRouteSequence() {
      this.simulatedPoints = []; // Reset waiting stops
      this.simulatedPaths = []; // Reset trips
      this.routePlayedItems = [];
      this.totalDist = 0; // Reset total distance for selected period
      this.totalTripDuration = 0;
      this.completedNodeIndices = {};
      this.revealedMarkerCount = 1; // Start with only origin marker visible
      this.simulatedCrashMarkers = []; // Reset progressive crash reveals

      this.marker = {
        position: null,
        icon: { path: this.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#1A8DE9", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
      };

      const timeline = this.selectedTrip.timeline;
      for (let i = 0; i < timeline.length; i++) {
        let r = timeline[i];

        let infoText = "";
        r.currentDist = 0;

        this.currentPlayingIndex = i;
        this.scrollToActiveNode(i);

        if (this.stopPlay) {
          this.isPlaying = false;
          this.isPaused = false;
          this.stopPlay = false;
          return;
        }

        if (r.type === "trip") {
          // Zoom map to fit this trip's bounds with smooth animation
          this.zoomToTripBounds(r);
          
          // Brief pause before starting trip for visual transition
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Update marker color for active trip
          this.marker.icon.fillColor = "#1A8DE9";

          this.simulatedPaths.push({
            path: [],
            geodesic: true,
            options: {
              strokeColor: "#1A8DE9",
              strokeOpacity: 1,
              strokeWeight: 5,
              zIndex: 999,
              icons: [{ icon: { path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 }, offset: "100%", repeat: "700px" }],
            },
            infoText: "",
          });

          for (let j = 0; j < r.path.length; j++) {
            while (this.isPaused) {
              await new Promise((resolve) => setTimeout(resolve, 100)); // Pause functionality
            }

            if (this.stopPlay) {
              this.isPlaying = false;
              this.isPaused = false;
              this.stopPlay = false;
              this.revealedMarkerCount = null;
              this.processRoutePlayedItems();
              return;
            }

            let point = r.path[j];

            // Constructing infoText for each point
            infoText = `<div style="font-family:'Segoe UI',system-ui,sans-serif;">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:7px;">
                <span style="background:#1A8DE9;color:white;font-size:10px;font-weight:700;padding:2px 9px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_history.trip_label').toUpperCase()} ${r.order}</span>
                <span style="font-size:12px;font-weight:600;color:#64748b;">${this.moment(r.startTime).format('HH:mm')} → ${this.moment(r.endTime).format('HH:mm')} · ${this.formatDist(r.distance)} km</span>
              </div>
              <div style="font-size:12px;color:#1e293b;margin-bottom:8px;">
                <span style="color:#00C16E;font-weight:600;">${this.formatAdd(r.startAddress)}</span>
                <span style="color:#94a3b8;margin:0 5px;">→</span>
                <span style="color:#ef4444;font-weight:600;">${this.formatAdd(r.endAddress)}</span>
              </div>
              <div style="display:flex;gap:4px;border-top:1px solid #f1f5f9;padding-top:7px;">
                <div style="flex:1;text-align:center;background:#FFF7ED;border-radius:8px;padding:5px 3px;">
                  <div style="font-size:13px;font-weight:800;color:#F29100;">${this.moment(point.time).format('HH:mm:ss')}</div>
                  <div style="font-size:9px;color:#F29100;opacity:.75;text-transform:uppercase;letter-spacing:.05em;margin-top:1px;">${this.$t('trip_history.time_label')}</div>
                </div>
                <div style="flex:1;text-align:center;background:#EBF5FF;border-radius:8px;padding:5px 3px;">
                  <div style="font-size:13px;font-weight:800;color:#1A8DE9;">${this.formatDist(point.dist)} <span style="font-size:10px;font-weight:600;">km</span></div>
                  <div style="font-size:9px;color:#1A8DE9;opacity:.75;text-transform:uppercase;letter-spacing:.05em;margin-top:1px;">${this.$t('trip_history.dist_label')}</div>
                </div>
                <div style="flex:1;text-align:center;background:#EEF2FF;border-radius:8px;padding:5px 3px;">
                  <div style="font-size:13px;font-weight:800;color:#1C497C;">${this.formatSpeed(point.speed)} <span style="font-size:10px;font-weight:600;">km/h</span></div>
                  <div style="font-size:9px;color:#1C497C;opacity:.75;text-transform:uppercase;letter-spacing:.05em;margin-top:1px;">${this.$t('trip_history.speed_label')}</div>
                </div>
              </div>
            </div>`;

            this.simulatedPaths[this.simulatedPaths.length - 1].path.push(point);

            if (!this.stopPlay) {
              await new Promise((resolve) => setTimeout(resolve, this.actualDelay));
            }

            // Update marker position and pan map to follow
            this.marker.position = point;
            this.panToMarker(point);

            // ── Reveal crash events as simulation reaches them ────
            const prevPoint = j > 0 ? r.path[j - 1] : null;
            await this._revealCrashEvents(point, prevPoint);

            this.stickyInfoContent = infoText;
            r.currentDist = point.dist;
          }

          // Finalizing each trip - use existing color from initial load
          let lastPathIndex = this.simulatedPaths.length - 1;
          this.simulatedPaths[lastPathIndex].options.strokeColor = r.color;
          this.simulatedPaths[lastPathIndex].infoText = infoText;
          r.infoText = infoText;
          r.position = r.startPosition;

          this.totalDist += r.distance;
          this.totalTripDuration += r.duration;

          // Mark origin/trip-start node as completed
          const originNode = this.timelineNodes.find(n => n.routeIndex === i);
          if (originNode) {
            this.completedNodeIndices = { ...this.completedNodeIndices, [i]: true };
          }

          // Brief pause after trip completes
          await new Promise((resolve) => setTimeout(resolve, 300));

        } else if (r.type == "waiting") {
          // Change marker to stop color
          this.marker.icon.fillColor = "#EF4444";
          this.marker.position = r.position;

          // Zoom to waiting position
          this.zoomToPosition(r.position, 16);
          
          // Brief pause for visual transition
          await new Promise((resolve) => setTimeout(resolve, 400));

          infoText = `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:190px;padding:6px 4px;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
              <span style="background:#F29100;color:white;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_history.stop_label').toUpperCase()} ${r.order}</span>
              <span style="background:#FFF7ED;color:#F29100;font-size:11px;font-weight:700;padding:2px 9px;border-radius:6px;">${this.moment.duration(r.duration, 'seconds').humanize()}</span>
            </div>
            <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${this.formatAdd(r.address)}</div>
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(r.startTime).format('HH:mm')}</span>
              <span style="color:#cbd5e1;font-size:12px;">→</span>
              <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(r.endTime).format('HH:mm')}</span>
            </div>
          </div>`;

          this.stickyInfoContent = infoText;

          this.simulatedPoints.push({
            ...r,
            options: {
              fillColor: "#EF4444",
              fillOpacity: 0.3,
              strokeColor: "#EF4444",
              strokeOpacity: 1,
              strokeWeight: 3,
              radius: 60,
            },
            infoText: infoText,
          });

          // Longer pause at stops to show waiting time visually
          const stopPauseDuration = Math.min(r.duration / 10, 2000); // Scale with actual wait time, max 2 seconds
          if (!this.stopPlay) {
            await new Promise((resolve) => setTimeout(resolve, Math.max(stopPauseDuration, 500)));
          }

          // Mark stop node as completed
          this.completedNodeIndices = { ...this.completedNodeIndices, [i]: true };
        }
      }

      // Mark final destination node as completed
      const finalNode = this.timelineNodes.find(n => n.nodeType === 'final');
      if (finalNode) {
        this.completedNodeIndices = { ...this.completedNodeIndices, [finalNode.routeIndex]: true };
      }

      // Reveal final destination marker
      this.revealedMarkerCount = null;

      this.isPlaying = false;
      this.currentPlayingIndex = -1;
      this.stickyInfoContent = `<div style="font-family:'Segoe UI',system-ui,sans-serif;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
          <span style="background:#00C16E;color:white;font-size:10px;font-weight:700;padding:2px 9px;border-radius:99px;letter-spacing:.04em;">✓ ${this.$t('trip_history.playback_complete').toUpperCase()}</span>
        </div>
        <div style="display:flex;gap:6px;">
          <div style="flex:1;text-align:center;background:#EBF5FF;border-radius:10px;padding:8px 6px;">
            <div style="font-size:18px;font-weight:800;color:#1A8DE9;letter-spacing:-.5px;line-height:1.1;">${this.formatDist(this.totalDist)}<span style="font-size:11px;font-weight:600;margin-left:2px;">km</span></div>
            <div style="font-size:9px;color:#1A8DE9;opacity:.75;text-transform:uppercase;letter-spacing:.07em;margin-top:3px;">${this.$t('trip_history.total_distance')}</div>
          </div>
          <div style="flex:1;text-align:center;background:#EEF2FF;border-radius:10px;padding:8px 6px;">
            <div style="font-size:15px;font-weight:800;color:#1C497C;letter-spacing:-.3px;line-height:1.2;">${this.moment.duration(this.totalTripDuration, 'seconds').humanize()}</div>
            <div style="font-size:9px;color:#1C497C;opacity:.75;text-transform:uppercase;letter-spacing:.07em;margin-top:3px;">${this.$t('trip_history.duration')}</div>
          </div>
        </div>
      </div>`;
      
      // Auto-hide sticky info after 3 seconds
      setTimeout(() => {
        if (!this.isPlaying) {
          this.stickyInfoContent = null;
        }
      }, 3000);
    },

    scrollToBottom() {
      this.$nextTick(function() {
        let el = this.$refs.rightSidebar;
        el.scrollTop = el.scrollHeight;
      });
    },

    scrollToActiveNode(routeIndex) {
      this.$nextTick(() => {
        const el = document.getElementById('jf-leg-' + routeIndex) || document.getElementById('jf-node-' + routeIndex);
        const container = this.$refs.rightSidebar;
        if (el && container) {
          const elTop = el.offsetTop - container.offsetTop;
          container.scrollTo({ top: elTop - container.clientHeight / 3, behavior: 'smooth' });
        }
      });
    },

    zoomToTripBounds(trip) {
      if (!trip.path || trip.path.length === 0) return;

      const bounds = new this.google.maps.LatLngBounds();
      
      // Include start and end positions
      if (trip.startPosition) bounds.extend(trip.startPosition);
      if (trip.endPosition) bounds.extend(trip.endPosition);
      
      // Include all path points for accurate bounds
      trip.path.forEach((point) => {
        if (point.lat && point.lng) {
          bounds.extend({ lat: point.lat, lng: point.lng });
        }
      });

      this.$refs.tripHistoryMap.$mapObject.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    },

    zoomToPosition(position, zoomLevel = 15) {
      if (!position) return;
      this.$refs.tripHistoryMap.$mapObject.panTo(position);
      this.$refs.tripHistoryMap.$mapObject.setZoom(zoomLevel);
    },

    panToMarker(point) {
      if (!point) return;
      const map = this.$refs.tripHistoryMap.$mapObject;
      const bounds = map.getBounds();
      
      if (bounds) {
        const position = new this.google.maps.LatLng(point.lat, point.lng);
        // Only pan if marker is near edge of visible area
        if (!bounds.contains(position)) {
          map.panTo(position);
        }
      }
    },

    getRandomPathColor() {
      // Generate blue-ish colors only
      const blueColors = [
        "#1E40AF", // Blue 800
        "#1D4ED8", // Blue 700
        "#2563EB", // Blue 600
        "#3B82F6", // Blue 500
        "#0EA5E9", // Sky 500
        "#0284C7", // Sky 600
        "#0369A1", // Sky 700
        "#0891B2", // Cyan 600
        "#0E7490", // Cyan 700
        "#06B6D4", // Cyan 500
        "#14B8A6", // Teal 500
        "#0D9488", // Teal 600
        "#0F766E", // Teal 700
        "#6366F1", // Indigo 500
        "#4F46E5", // Indigo 600
        "#4338CA", // Indigo 700
        "#7C3AED", // Violet 600
        "#8B5CF6", // Violet 500
      ];
      return blueColors[Math.floor(Math.random() * blueColors.length)];
    },

    ChangePathColor() {
      this.pathColorChanged = !this.pathColorChanged;
      // this.pathColor.activeColor = this.pathColor.activeColor === this.pathColor.regularColor ? this.pathColor.changedColor : this.pathColor.regularColor;
      // this.optimizedPathColor.activeColor = this.optimizedPathColor.activeColor === this.optimizedPathColor.regularColor ? this.optimizedPathColor.changedColor : this.optimizedPathColor.regularColor;

      const timeline = this.selectedTrip?.timeline || [];
      if (this.pathColorChanged) {
        const colors = timeline.map(() => this.getRandomPathColor());
        timeline.forEach((r, index) => {
          const color = colors[index];
          r.options = {
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: 3,
          };
        });
      } else {
        timeline.forEach((r) => {
          r.options = {
            strokeColor: "red",
            strokeOpacity: 1,
            strokeWeight: 3,
          };
        });
      }
    },

    copyDirectionLink() {
      var vm = this;
      
      // Check if we have route data
      const timeline = this.selectedTrip?.timeline || [];
      if (!timeline.length) {
        this.handleError(null, 'No route available', 'top-right', 2000);
        return;
      }

      // Get origin and destination from route results
      let originPos = null;
      let destinationPos = null;
      let waypointsList = [];

      // Find first position (origin)
      const firstItem = timeline[0];
      if (firstItem.type === 'trip') {
        originPos = firstItem.startPosition;
      } else if (firstItem.type === 'waiting') {
        originPos = firstItem.position;
      }

      // Find last position (destination)
      const lastItem = timeline[timeline.length - 1];
      if (lastItem.type === 'trip') {
        destinationPos = lastItem.endPosition;
      } else if (lastItem.type === 'waiting') {
        destinationPos = lastItem.position;
      }

      // Collect intermediate waypoints from waiting stops
      for (let i = 1; i < timeline.length - 1; i++) {
        const item = timeline[i];
        if (item.type === 'waiting' && item.position) {
          waypointsList.push(item.position);
        }
      }

      // Validate positions
      if (!originPos || !destinationPos) {
        this.handleError(null, 'Invalid route data', 'top-right', 2000);
        return;
      }

      let origin = `${originPos.lat},${originPos.lng}`;
      let destination = `${destinationPos.lat},${destinationPos.lng}`;

      // Google Maps: pipe-separated waypoints, only added if present
      const googleWaypoints = waypointsList.map(w => `${w.lat},${w.lng}`).join('|');
      let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
      if (googleWaypoints) url += `&waypoints=${googleWaypoints}`;

      if (vm.checkIOS()) {
        url = `https://maps.apple.com/directions?source=${origin}&destination=${destination}&mode=driving`;
        waypointsList.forEach(w => {
          url += `&waypoint=${w.lat},${w.lng}`;
        });
      }
      const cb = navigator.clipboard;
      cb.writeText(url).then(() => vm.handleSuccess(this.$t('success_messages.directions_copied')));
    },
    checkIOS() {
      const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    },

    formatAdd: function(value) {
      if (!value) return "";
      value = value.toString();
      return value.substring(0, value.indexOf(","));
    },

    formatDist(value) {
      return (value / 1000).toFixed(2);
    },

    formatSpeed(value) {
      return Math.ceil(value);
    },

    checkDepartureTime() {
      let departureTimeMoment = this.moment(this.departureTime);
      let now = this.moment();
      if (departureTimeMoment.isBefore(now)) {
        let t = 15 - (now.minutes() % 15);
        now.add(t, "minutes");
        this.departureTime = new Date(now);
      }
    },

    initDate() {
      this.departureTime = new Date(
        this.moment()
          .add(2, "hours")
          .startOf("hour")
      );
    },

    stopAnimation() {
      this.stopPlay = !this.stopPlay;

      if (this.stopPlay) {
        this.isPlaying = false;
        this.isPaused = false;
        this.simulatedCrashMarkers = [];
        this.playRouteSequence();
        this.stopPlay = false;
      }
    },
    playAnimation() {
      this.stopPlay = false;
      this.currentPlayingIndex = -1; // Reset individual play index when using global play
      // If not currently playing, start the animation
      if (!this.isPlaying && !this.isPaused) {
        this.isPlaying = true;
        this.isPaused = false;
        this.playRouteSequence();
      } else if (this.isPlaying && !this.isPaused) {
        // If currently playing and not paused, pause the animation
        this.isPaused = true;
      } else if (this.isPlaying && this.isPaused) {
        // If currently paused, resume the animation
        this.isPaused = false;
      }
    },

    async playIndividualItem(index) {
      const item = (this.selectedTrip?.timeline || [])[index];
      if (!item) return;

      // If clicking on the same item that's playing, toggle pause
      if (this.currentPlayingIndex === index && this.isPlaying) {
        this.isPaused = !this.isPaused;
        return;
      }

      // Stop any current playback
      if (this.isPlaying) {
        this.stopPlay = true;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Reset state
      this.stopPlay = false;
      this.isPlaying = true;
      this.isPaused = false;
      this.currentPlayingIndex = index;
      this.simulatedCrashMarkers = []; // reset crash reveals for individual playback

      // Initialize marker
      this.marker = {
        position: null,
        icon: { path: this.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#1A8DE9", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
      };

      if (item.type === "trip") {
        await this.playIndividualTrip(item, index);
      } else if (item.type === "waiting") {
        await this.playIndividualStop(item);
      }

      this.isPlaying = false;
      this.currentPlayingIndex = -1;
    },

    async playIndividualTrip(trip, index) {
      // Zoom to trip bounds
      this.zoomToTripBounds(trip);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Update marker color
      this.marker.icon.fillColor = "#1A8DE9";

      // Create path for this trip
      const tripPath = {
        path: [],
        geodesic: true,
        options: {
          strokeColor: "#1A8DE9",
          strokeOpacity: 1,
          strokeWeight: 4,
          zIndex: 999,
          icons: [{ icon: { path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 }, offset: "100%", repeat: "700px" }],
        },
        infoText: "",
      };

      // Add to tracked paths temporarily
      const pathIndex = this.simulatedPaths.length;
      this.simulatedPaths.push(tripPath);

      trip.currentDist = 0;

      for (let j = 0; j < trip.path.length; j++) {
        while (this.isPaused) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        if (this.stopPlay || this.currentPlayingIndex !== index) {
          return;
        }

        let point = trip.path[j];

        const infoText = `<div style="padding: 8px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;;; max-width: 320px;">
          <strong style="font-size: 13px; font-weight: 600; color: #1A8DE9;">Trip ${trip.order}</strong><br>
          <span style="color: #00C16E; font-weight: 500;">${this.formatAdd(trip.startAddress)}</span> → <span style="color: #ef4444; font-weight: 500;">${this.formatAdd(trip.endAddress)}</span><br>
          <small style="color: #666; font-size: 12px;">${this.moment(trip.startTime).format("HH:mm")} → ${this.moment(trip.endTime).format("HH:mm")}, ${this.moment.duration(trip.duration, "seconds").humanize()}, ${this.formatDist(trip.distance)} km</small><br>
          <strong style="color: #F29100; font-size: 14px;">${this.moment(point.time).format("HH:mm:ss")} | ${this.formatDist(point.dist)} km | ${this.formatSpeed(point.speed)} km/h</strong>
        </div>`;

        this.simulatedPaths[pathIndex].path.push(point);

        if (!this.stopPlay) {
          await new Promise((resolve) => setTimeout(resolve, this.actualDelay));
        }

        this.marker.position = point;
        this.panToMarker(point);

        // ── Reveal crash events as simulation reaches them ────
        const prevPt = j > 0 ? trip.path[j - 1] : null;
        await this._revealCrashEvents(point, prevPt);

        this.stickyInfoContent = infoText;
        trip.currentDist = point.dist;
      }

      // Finalize trip - use existing color from initial load
      this.simulatedPaths[pathIndex].options.strokeColor = trip.color;
    },

    async playIndividualStop(stop) {
      // Change marker to stop color
      this.marker.icon.fillColor = "#EF4444";
      this.marker.position = stop.position;

      // Zoom to stop
      this.zoomToPosition(stop.position, 16);
      await new Promise((resolve) => setTimeout(resolve, 400));

      const infoText = `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:190px;padding:6px 4px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="background:#F29100;color:white;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:.04em;">${this.$t('trip_history.stop_label').toUpperCase()} ${stop.order}</span>
          <span style="background:#FFF7ED;color:#F29100;font-size:11px;font-weight:700;padding:2px 9px;border-radius:6px;">${this.moment.duration(stop.duration, 'seconds').humanize()}</span>
        </div>
        <div style="font-size:11px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${this.formatAdd(stop.address)}</div>
        <div style="display:flex;align-items:center;gap:6px;">
          <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(stop.startTime).format('HH:mm')}</span>
          <span style="color:#cbd5e1;font-size:12px;">→</span>
          <span style="background:#EBF5FF;color:#1A8DE9;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;">${this.moment(stop.endTime).format('HH:mm')}</span>
        </div>
      </div>`;

      // Add to tracked points
      this.simulatedPoints.push({
        ...stop,
        options: {
          fillColor: "#EF4444",
          fillOpacity: 0.3,
          strokeColor: "#EF4444",
          strokeOpacity: 1,
          strokeWeight: 3,
          radius: 60,
        },
        infoText: infoText,
      });

      // Wait for visual
      const stopPauseDuration = Math.min(stop.duration / 10, 2000);
      await new Promise((resolve) => setTimeout(resolve, Math.max(stopPauseDuration, 1000)));

      setTimeout(() => {
        if (!this.isPlaying) {
          this.stickyInfoContent = null;
        }
      }, 3000);
    },

    reset() {
      this.pinnedTrackedPointInfo = false;
      this.trackedRoute = [];
      this.simulatedPoints = [];
      this.simulatedPaths = [];
      this.alarm.unit_name = "";
      this.alarm.product_name = "";
      this.currentPlayingIndex = -1;
      this.isPlaying = false;
      this.isPaused = false;
      this.stopPlay = false;
      this.selectedTrip = null;
      this.hasFetchedData = false;
      this.stickyInfoContent = null;
      this.completedNodeIndices = {};
      this.revealedMarkerCount = null;
      this.crashMarkers = [];
    },

    closeInfoWindow() {
      this.infoWinOpen = false;
      this.infoWinLocked = false;
      this.isCrashHovered = false;
      this.isCrashPinned = false;
    },

    showInfoWindow(event, infoText, show, index = -1) {
      if (this.isCrashHovered || this.isCrashPinned) return;
      if (show) {
        // set (hover overrides lock)
        this.infoWinLocked = false;
        this.infoWindowPos = event.latLng;
        this.infoOptions.content = infoText;
        this.infoWinOpen = show;
        if (index > -1) {
          this.simulatedPaths[index].options.strokeWeight = 4;
          this.simulatedPaths[index].options.strokeOpacity = 1;

          for (let i = 0; i < this.simulatedPaths.length; i++) {
            let p = this.simulatedPaths[i];
            if (i != index) {
              p.options.strokeOpacity = 0.2;
              p.options.strokeWeight = 2.5;
            }
          }
        }
      } else {
        // reset — but don't close if user clicked to lock the InfoWindow
        if (!this.infoWinLocked) {
          this.infoWinOpen = false;
        }
        for (let i = 0; i < this.simulatedPaths.length; i++) {
          let p = this.simulatedPaths[i];
          p.options.strokeOpacity = 1;
          p.options.strokeWeight = 2.5;
        }
      }
    },

    showRouteInfoWindow(event, infoText, show, index = -1) {
      if (this.isCrashHovered || this.isCrashPinned) return;
      if (show) {
        // set (hover overrides lock)
        this.infoWinLocked = false;
        this.infoWindowPos = event.latLng;
        this.infoOptions.content = infoText;
        this.infoWinOpen = show;
        if (index > -1) {
          const tl = this.selectedTrip?.timeline || [];
          const selectedRoute = tl[index];
          const updatedOptions = {
            ...selectedRoute?.options,
            strokeWeight: 4,
            strokeOpacity: 1,
          };
          if (tl[index]) tl[index].options = updatedOptions;
          for (let i = 0; i < tl.length; i++) {
            if (i !== index) {
              const otherRoute = tl[i];
              const updatedOptions = {
                ...otherRoute?.options,
                strokeWeight: 3,
                strokeOpacity: 0.2,
              };
              tl[i].options = updatedOptions;
            }
          }
        }
      } else {
        // reset — but don't close if user clicked to lock the InfoWindow
        if (!this.infoWinLocked) {
          this.infoWinOpen = false;
        }
        (this.selectedTrip?.timeline || []).forEach((p) => {
          p.options = {
            ...p.options,
            strokeWeight: 3,
            strokeOpacity: 1,
          };
        });
      }
    },

    focusWaypointNode(node) {
      if (!node || !node.position) return;
      const vm = this;
      setTimeout(() => {
        vm.$refs.tripHistoryMap.$mapObject.panTo(node.position);
        vm.$refs.tripHistoryMap.$mapObject.setZoom(15);
        vm.infoWindowPos  = node.position;  // {lat, lng} — correct for GmapInfoWindow
        vm.infoOptions.content = node.infoText || '';
        vm.infoWinOpen = true;
        vm.infoWinLocked = true;
      }, 300);
    },

    setupModal(alram_data) {
      this.initDate();
      this.isPlaying = false;
      this.infoWinOpen = false;
      this.infoWinLocked = false;
      this.infoOptions.content = null;
      this.zoom = this.$store.state.mapZoom;
      this.center = this.$store.state.mapCenter;
      this.alarm.unit_name = alram_data.unit_name;
      this.alarm.product_name = alram_data.product_name;
      this.tripId = alram_data._id?.$oid || alram_data._id || alram_data.trip_id || null;

      this.simulatedPoints = [];
      this.simulatedPaths = [];
      this.waypoints = [];
      this.startPosition = null;
      this.endPosition = null;

      this.getRouteResult();
    },
    hide() {
      this.$modal.hide("modal-trip-history2");
    },
    disableFutureDate(date) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return date > today;
    },

    async selectTrip(trip) {
      if (this.isPlaying) {
        this.stopPlay = true;
        this.isPlaying = false;
        this.isPaused = false;
      }
      this.selectedTrip = trip;
      this.crashMarkers = trip.crashEvents || [];
      // Reset animation + map state
      this.stopPlay = false;
      this.currentPlayingIndex = -1;
      this.simulatedPoints = [];
      this.simulatedPaths = [];
      this.infoWinOpen = false;
      this.stickyInfoContent = null;
      this.pathColorChanged = false;
      this.startPosition = null;
      this.endPosition = null;
      this.waypoints = [];
      this.mapKey = Date.now();
      // Geocode any missing addresses in the timeline (API trips start with empty strings)
      await this.geocodeTimelineAddresses();
      // Fit map to trip bounds
      if (trip.timeline.length > 0) {
        const first = trip.timeline[0];
        const last = trip.timeline[trip.timeline.length - 1];
        this.mapStartPosition = first.type === 'trip' ? first.startPosition : first.position;
        this.mapEndPosition = last.type === 'trip' ? last.endPosition : last.position;
        this.setMapBounds();
      }
    },

    // ─────────────────────────────────────────────────────────────
    // Small API-normalisation helpers
    // ─────────────────────────────────────────────────────────────

    // Parse MongoDB { $date } objects, ISO strings, or Date instances
    _rawDate(val) {
      if (!val) return null;
      return new Date(val.$date ?? val);
    },

    // Normalise any position shape → { lat, lng }
    _normPos(pos) {
      if (!pos) return null;
      const lat = pos.lat ?? pos.latitude ?? null;
      const lng = pos.lng ?? pos.lon ?? pos.longitude ?? null;
      if (lat === null || lng === null) return null;
      return { lat: parseFloat(lat), lng: parseFloat(lng) };
    },

    // Find the tracked_route point whose timestamp is closest to `targetTime`.
    // Returns { lat, lng } or null if the route is empty.
    _posAtTime(trackedRoute, targetTime) {
      if (!trackedRoute || !trackedRoute.length) return null;
      let best = null, bestDiff = Infinity;
      for (const pt of trackedRoute) {
        const raw = pt.timestamp?.$date ?? pt.timestamp ?? pt.time?.$date ?? pt.time;
        if (!raw) continue;
        const diff = Math.abs(new Date(raw) - targetTime);
        if (diff < bestDiff) { bestDiff = diff; best = pt; }
      }
      return best ? this._normPos(best) : null;
    },

    // Converts one raw API trip object into the shape the component needs.
    transformTripData(trip) {
      const raw      = trip.tracked_route || [];
      const waypoints = (trip.waypoints || []).filter(wp => wp.address?.trim());

      // Sort log oldest → newest
      const log = [...(trip.trip_log || [])].sort(
        (a, b) => this._rawDate(a.time) - this._rawDate(b.time)
      );

      // Normalize event type strings
      const normType = t => {
        if (!t) return '';
        const s = t.toLowerCase().replace(/-/g, '_');
        if (['trip_start', 'start'].includes(s))              return 'trip_start';
        if (['trip_end', 'end', 'finished', 'completed'].includes(s)) return 'trip_end';
        if (/^stop_\d+$/.test(s))                             return 'waiting_stop';
        return s;
      };

      // Build flat timeline
      const timeline = [];
      let openDrive = null;
      let openStop  = null;
      let tripNum   = 0;
      let stopNum   = 0;

      const pushTrip = (endTime, endPos) => {
        if (!openDrive) return;
        const path = this.getPathForTimeRange(raw, openDrive.time, endTime);
        tripNum++;
        timeline.push({
          type: 'trip', order: tripNum, currentDist: 0,
          color: '#1A8DE9', options: { strokeColor: '#1A8DE9', strokeOpacity: 1, strokeWeight: 5 },
          startTime: openDrive.time.toISOString(), endTime: endTime.toISOString(),
          startPosition: openDrive.position, endPosition: endPos, position: openDrive.position,
          startAddress: waypoints[tripNum - 1]?.address || '',
          endAddress:   waypoints[tripNum]?.address     || '',
          distance: this.calculateDistance(path),
          duration: Math.max(0, (endTime - openDrive.time) / 1000),
          path,
        });
        openDrive = null;
      };

      const pushStop = (endTime) => {
        if (!openStop) return;
        stopNum++;
        timeline.push({
          type: 'waiting', order: stopNum, currentDist: 0,
          color: 'red', options: { strokeColor: 'red', strokeOpacity: 0, strokeWeight: 0 },
          startTime: openStop.time.toISOString(), endTime: endTime.toISOString(),
          position: openStop.position,
          address: '', waypointAddress: openStop.label || '',
          note: openStop.note || '', message: openStop.message || '',
          duration: Math.max(0, (endTime - openStop.time) / 1000),
          path: [],
        });
        openStop = null;
      };

      for (const ev of log) {
        const time = this._rawDate(ev.time);
        const pos  = this._normPos(ev.position);
        switch (normType(ev.type)) {
          case 'trip_start':
            pushStop(time);
            openDrive = { time, position: pos };
            break;
          case 'waiting_stop': {
            const stopPos = this._posAtTime(raw, time) || pos;
            pushStop(time);
            pushTrip(time, stopPos);
            openStop  = { time, position: stopPos, label: ev.message || ev.note || ev.address || '', note: ev.note || '', message: ev.message || '' };
            openDrive = { time, position: stopPos };
            break;
          }
          case 'trip_end':
            pushStop(time);
            pushTrip(time, pos);
            break;
        }
      }
      if (log.length > 0) {
        const lastTime = this._rawDate(log[log.length - 1].time);
        const lastPos  = this._normPos(log[log.length - 1].position);
        pushStop(lastTime);
        pushTrip(lastTime, lastPos);
      }

      // Extract crash_detected events from trip_log
      const crashEvents = log
        .filter(ev => {
          const t = normType(ev.type);
          return t === 'crash_detected' || t === 'crash';
        })
        .map(ev => {
          const time = this._rawDate(ev.time);
          const pos  = this._normPos(ev.position) || this._posAtTime(raw, time);
          if (!pos) return null;
          return {
            position: pos,
            time:     time ? time.toISOString() : null,
            note:     ev.note || ev.message || '',
            address:  '',
          };
        })
        .filter(Boolean);

      // Totals
      const depRaw  = trip.departure_time?.$date || trip.departure_time;
      const lastLog = log[log.length - 1];
      const endRaw  = lastLog ? (lastLog.time?.$date || lastLog.time) : depRaw;
      const distance = trip.total_distance || timeline.filter(s => s.type === 'trip').reduce((sum, s) => sum + s.distance, 0);
      const duration = trip.total_duration || Math.max(0, (new Date(endRaw) - new Date(depRaw)) / 1000);

      return {
        id:            trip._id?.$oid || trip._id || Date.now(),
        label:         trip.route_name || `Trip ${this.moment(depRaw).format('MMM D')}`,
        date:          this.moment(depRaw).format('YYYY-MM-DD'),
        startTime:     this.moment(depRaw).format(),
        endTime:       this.moment(endRaw).format(),
        totalDistance: distance,
        totalDuration: duration,
        color:         '#1A8DE9',
        startAddress:  trip.start_address || '',
        endAddress:    trip.end_address   || '',
        timeline,
        crashEvents,
      };
    },

    // Extract GPS points from tracked_route that fall within [startTime, endTime].
    // Duplicates (same timestamp appearing more than once in the raw data) are removed
    // so the animated path never doubles back on itself.
    getPathForTimeRange(trackedRoute, startTime, endTime) {
      const seenTs = new Set();
      return (trackedRoute || [])
        .map(pt => {
          // Support multiple timestamp field names
          const rawT = pt.timestamp?.$date ?? pt.timestamp ?? pt.time?.$date ?? pt.time ?? null;
          if (!rawT) return null;
          const t = new Date(rawT);
          if (isNaN(t) || t < startTime || t > endTime) return null;

          const lat = pt.lat ?? pt.latitude ?? null;
          const lng = pt.lng ?? pt.lon ?? pt.longitude ?? null;
          if (lat === null || lng === null) return null;

          return {
            lat:    parseFloat(lat),
            lng:    parseFloat(lng),
            time:   t.toISOString(),
            _sortT: t,
            _tsKey: t.getTime(),
            dist:   0,
            speed:  pt.speed ?? pt.Speed ?? pt.speed_kmh ?? 0,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a._sortT - b._sortT)
        // Remove entries whose timestamp has already been seen (de-duplicate)
        .filter(pt => {
          if (seenTs.has(pt._tsKey)) return false;
          seenTs.add(pt._tsKey);
          return true;
        })
        .map(p => { delete p._sortT; delete p._tsKey; return p; });
    },

    // Walk the path and set cumulative dist (metres) on each point; return total
    calculateDistance(path) {
      let total = 0;
      for (let i = 1; i < path.length; i++) {
        total += this.haversineDistance(path[i - 1], path[i]);
        path[i].dist = total;
      }
      return total;
    },

    // Haversine formula – returns distance in metres between two {lat,lng} points
    haversineDistance(a, b) {
      const R = 6371000; // Earth radius in metres
      const toRad = d => d * Math.PI / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const x = Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
                Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    },
    showTrackedPointInfo(point, index) {
      if (this.isCrashHovered || this.isCrashPinned) return;
      const time  = point?.timestamp ? moment(point.timestamp).tz('Europe/Stockholm').format('HH:mm:ss') : '—';
      const speed = point?.speed != null ? point.speed.toFixed(1) : '—';
      const lat   = point?.lat  != null ? point.lat.toFixed(6)  : '—';
      const lng   = point?.lng  != null ? point.lng.toFixed(6)  : '—';
      const fuel  = point?.fuelLevel != null ? point.fuelLevel : null;

      const fuelColor = fuel == null ? '#94a3b8' : fuel > 50 ? '#00C16E' : fuel > 20 ? '#F29100' : '#ef4444';
      const fuelBar = fuel != null ? `
        <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;grid-column:span 2;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px;">
            <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;">Fuel Level</div>
            <div style="font-size:13px;font-weight:700;color:${fuelColor};">${fuel}<span style="font-size:10px;font-weight:500;color:#94a3b8;"> %</span></div>
          </div>
          <div style="height:6px;background:#e2e8f0;border-radius:99px;overflow:hidden;">
            <div style="height:100%;width:${Math.min(fuel,100)}%;background:${fuelColor};border-radius:99px;transition:width .3s;"></div>
          </div>
        </div>` : '';

      const infoText = `
        <div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:200px;padding:2px 0;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <div>
              <div style="font-size:11px;font-weight:600;color:#1A8DE9;letter-spacing:.06em;text-transform:uppercase;">Route Point</div>
              <div style="font-size:15px;font-weight:700;color:#1e293b;line-height:1.2;">#${index}</div>
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">Time</div>
              <div style="font-size:13px;font-weight:700;color:#1e293b;">${time}</div>
            </div>
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">Speed</div>
              <div style="font-size:13px;font-weight:700;color:#F29100;">${speed} <span style="font-size:10px;font-weight:500;color:#94a3b8;">km/h</span></div>
            </div>
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">Lat</div>
              <div style="font-size:12px;font-weight:600;color:#475569;font-variant-numeric:tabular-nums;">${lat}</div>
            </div>
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">Lng</div>
              <div style="font-size:12px;font-weight:600;color:#475569;font-variant-numeric:tabular-nums;">${lng}</div>
            </div>
            ${fuelBar}
          </div>
        </div>
      `;

      this.infoWindowPos = point;
      this.infoOptions.content = infoText;
      this.infoWinOpen = true;
    },
    closeTrackedPointInfoUnlessPinned() {
      if (!this.pinnedTrackedPointInfo) {
        this.infoWinOpen = false;
      }
    },
    pinTrackedPointInfo(point, index) {
      this.showTrackedPointInfo(point, index);
      this.pinnedTrackedPointInfo = true;
    },

    // ── Crash Detected Markers ───────────────────────────────────
    getCrashMarkerIcon() {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52" width="40" height="52">
        <!-- Drop shadow -->
        <ellipse cx="20" cy="50" rx="7" ry="2.5" fill="rgba(0,0,0,0.18)"/>
        <!-- Pin body: circle top + triangle tip -->
        <path d="M20 2 C10.06 2 2 10.06 2 20 C2 31 20 50 20 50 C20 50 38 31 38 20 C38 10.06 29.94 2 20 2 Z" fill="#DC2626"/>
        <!-- Subtle inner sheen -->
        <path d="M20 4 C11.16 4 4 11.16 4 20 C4 30.5 20 48 20 48 C20 48 36 30.5 36 20 C36 11.16 28.84 4 20 4 Z" fill="none" stroke="rgba(255,255,255,0.18)" stroke-width="1.5"/>
        <!-- White circle badge -->
        <circle cx="20" cy="19" r="11" fill="#ffffff"/>
        <!-- Warning / crash icon: bold exclamation mark -->
        <rect x="18.2" y="11.5" width="3.6" height="9" rx="1.8" fill="#DC2626"/>
        <circle cx="20" cy="24" r="2" fill="#DC2626"/>
      </svg>`;
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new this.google.maps.Size(34, 44),
        anchor: new this.google.maps.Point(17, 44),
      };
    },

    getCrashInfoText(crash) {
      const time    = crash.time ? this.moment(crash.time).format('D MMM YYYY, HH:mm:ss') : '—';
      const address = crash.address
        || (crash.position ? `${crash.position.lat.toFixed(5)}, ${crash.position.lng.toFixed(5)}` : '—');

      return `<div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:230px;padding:8px 4px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="background:#DC2626;color:white;font-size:10px;font-weight:700;padding:4px 11px;border-radius:99px;letter-spacing:.05em;">⚠ CRASH DETECTED</span>
        </div>
        <div style="font-size:12px;font-weight:400;color:#475569;margin-bottom:8px;line-height:1.5;">${address}</div>
        <span style="display:inline-block;background:#FEF2F2;color:#DC2626;font-size:11px;font-weight:600;padding:3px 11px;border-radius:6px;">${time}</span>
      </div>`;
    },

    // Reveals any crash events whose timestamp falls between prevPoint and curPoint.
    // Flashes a crash alert in the live-info-card for 2.5 s per event.
    async _revealCrashEvents(curPoint, prevPoint) {
      const curMs  = curPoint?.time  ? new Date(curPoint.time).getTime()  : -1;
      const prevMs = prevPoint?.time ? new Date(prevPoint.time).getTime() : -Infinity;
      if (curMs < 0) return;

      const newCrashes = this.crashMarkers.filter(c => {
        const ct = c.time ? new Date(c.time).getTime() : -1;
        return ct > prevMs && ct <= curMs && !this.simulatedCrashMarkers.includes(c);
      });

      for (const crash of newCrashes) {
        this.simulatedCrashMarkers.push(crash);
        const addr = crash.address ||
          (crash.position ? `${crash.position.lat.toFixed(5)}, ${crash.position.lng.toFixed(5)}` : '—');
        const time = crash.time ? this.moment(crash.time).format('HH:mm:ss') : '—';
        this.stickyInfoContent = `
          <div style="font-family:'Segoe UI',system-ui,sans-serif;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
              <span style="background:#DC2626;color:white;font-size:10px;font-weight:700;padding:3px 11px;border-radius:99px;letter-spacing:.05em;">⚠ CRASH DETECTED</span>
              <span style="font-size:11px;font-weight:700;color:#DC2626;">${time}</span>
            </div>
            <div style="font-size:11px;color:#475569;line-height:1.5;">${addr}</div>
          </div>`;
        await new Promise(r => setTimeout(r, 2500));
      }
    },

    showCrashInfoWindow(crash) {
      if (this.isCrashPinned) return;
      this.isCrashHovered      = true;
      this.infoWindowPos       = crash.position;
      this.infoOptions.content = this.getCrashInfoText(crash);
      this.infoWinOpen         = true;
    },
    hideCrashInfoWindow() {
      if (this.isCrashPinned) return;
      this.isCrashHovered      = false;
      this.infoWinOpen         = false;
      this.infoOptions.content = '';
    },
    pinCrashInfoWindow(crash) {
      this.isCrashPinned       = true;
      this.isCrashHovered      = true;
      this.infoWindowPos       = crash.position;
      this.infoOptions.content = this.getCrashInfoText(crash);
      this.infoWinOpen         = true;
    },
    onMapClick() {
      if (this.isCrashPinned) {
        this.isCrashPinned       = false;
        this.isCrashHovered      = false;
        this.infoWinOpen         = false;
        this.infoOptions.content = '';
        return;
      }
      // also dismiss pinned tracked-point info
      this.pinnedTrackedPointInfo = false;
      this.infoWinOpen = false;
    },
  },

  created() {},

  mounted() {},
};
</script>
<style scoped>
/* ── Modal wrapper ─────────────────────────────────────────── */
.trip-history { overflow: hidden; }
.trip-history ::v-deep .vm--modal {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 0;
}

/* ── Header ────────────────────────────────────────────────── */
.trip-header {
  flex-shrink: 0;
  height: 56px;
  box-shadow: 0 1px 0 #f0f0f0;
  z-index: 20;
}

/* ── Body ───────────────────────────────────────────────────── */
.trip-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
  height: calc(100vh - 56px);
}

/* ── Left Panel ─────────────────────────────────────────────── */
.left-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #e5e5e5;
  /* Mobile: horizontal strip at top */
  width: 100%;
  max-height: 45vh;
}
@media (min-width: 1024px) {
  .left-panel {
    width: 350px;
    min-width: 350px;
    max-height: none;
    height:calc(100vh - 16%)
  }
}
@media (min-width: 1200px) {
  .left-panel {
    width: 390px;
    min-width: 390px;
    max-height: none;
    height:calc(100vh - 12%)
  }
}
/* ── Trip list scrollable ───────────────────────────────────── */
.trip-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-top: 6px;
  scrollbar-width: thin;
  scrollbar-color: #d4d4d4 transparent;
}
.trip-list::-webkit-scrollbar { width: 4px; }
.trip-list::-webkit-scrollbar-track { background: transparent; }
.trip-list::-webkit-scrollbar-thumb { background: #d4d4d4; border-radius: 2px; }
.trip-list::-webkit-scrollbar-thumb:hover { background: #a3a3a3; }

/* ── Map panel ──────────────────────────────────────────────── */
.map-panel {
  flex: 1;
  overflow: hidden;
  min-height: 300px;
}
@media (max-width: 1023px) {
  .map-panel { min-height: 55vh; }
}

/* ── Stat pills ─────────────────────────────────────────────── */
.stat-pill { transition: transform 0.15s ease; }
.stat-pill:hover { transform: scale(1.02); }

/* ── Trip card ──────────────────────────────────────────────── */
.trip-card { cursor: default; }
.trip-badge { letter-spacing: -0.02em; }

/* ── Activity Bar ───────────────────────────────────────────── */
/*
  Samsara-style horizontal strip at the top of the left panel.
  Each coloured block = one trip leg or waiting stop, width ∝ duration.
  Green/blue = moving, amber = short stop, red = long stop.
*/
.activity-bar-wrap {
  padding-bottom: 2px;
}
.activity-bar {
  border-radius: 6px;
  overflow: hidden;
  background: #f1f5f9; /* visible gap colour between segments */
}
.activity-seg {
  display: block;
  flex-shrink: 0;
}
.activity-seg:first-child { border-radius: 6px 0 0 6px; }
.activity-seg:last-child  { border-radius: 0 6px 6px 0; }
.activity-needle {
  width: 2px;
  border-radius: 1px;
  box-shadow: 0 0 0 2px rgba(255,255,255,0.7), 0 2px 6px rgba(0,0,0,0.25);
  transition: left 0.3s linear;
}

/* ── Journey Flow Timeline (RoutePreviewTimeline design) ───── */
.journey-flow {
  padding: 20px 0px 0px 45px;
  position: relative;
}

/* Main vertical dashed line */
.journey-flow::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 50px;
  bottom: var(--line-bottom, 50px);
  width: 2px;
  background-image: linear-gradient(to bottom, #9CA3AF 50%, transparent 50%);
  background-size: 2px 12px;
  z-index: 0;
}

.route-waypoint-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 24px;
}

/* Icon circles */
.route-icon-circle {
  position: absolute;
  left: -43px;
  top: 10px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background: #9CA3AF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.route-icon-start {
  background: #3b82f6;
}

.route-icon-end {
  background: #F59E0B;
}

.route-icon-stop {
  background: #3b82f6;
}

.route-icon-pending {
  background: #9CA3AF !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
}

.route-icon-active {
  background: #1A8DE9 !important;
  box-shadow: 0 0 0 4px rgba(26, 141, 233, 0.2), 0 4px 12px rgba(26, 141, 233, 0.4);
  transform: scale(1.1);
  animation: icon-ring-pulse 1.6s ease-in-out infinite;
}
@keyframes icon-ring-pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(26, 141, 233, 0.2), 0 4px 12px rgba(26, 141, 233, 0.4); }
  50%       { box-shadow: 0 0 0 9px rgba(26, 141, 233, 0.08), 0 4px 16px rgba(26, 141, 233, 0.5); }
}

/* Route point cards */
.route-point {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  margin: 0;
  background: white;
  border: 1px solid #E5E7EB;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex: 1;
}

/* Triangular pointer */
.route-point::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 20px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 12px solid white;
  filter: drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.05));
}

.route-point:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateX(4px);
}

.route-point.bg-blue-50 {
  background: white;
  border-left: 4px solid #3b82f6;
}

.route-point.bg-amber-50 {
  background: white;
  border-left: 4px solid #F59E0B;
}

/* .route-point.active-playing {
  background: rgba(26, 141, 233, 0.04) !important;
  border-left-color: #F59E0B !important;
  border-left-width: 4px;
  box-shadow: 0 4px 18px rgba(26, 141, 233, 0.18);
} */


.route-point > .flex-1 {
  min-width: 0;
}

.route-point .text-xxs {
  font-size: 11px;
  letter-spacing: 0.05em;
  font-weight: 700;
  margin-bottom: 4px;
}

.route-point .address {
  font-size: 14px;
  color: #374151;
  font-weight: 400;
  line-height: 1.5;
}

/* Waypoint dot scale-up on hover */
.route-waypoint-main:hover .route-icon-circle {
  transform: scale(1.15);
}

/* Legacy styles for compatibility */
.jf-node:hover .jf-dot {
  transform: scale(1.15);
  transition: transform 0.15s ease;
}
.jf-dot {
  transition: transform 0.15s ease;
}

/* Leg card subtle lift on hover */
.jf-leg-card {
  will-change: box-shadow;
}

/* Waypoint node card */
.jf-node-card {
  cursor: default;
  will-change: box-shadow;
}
.jf-node-card:hover {
  box-shadow: 0 2px 8px rgba(26, 141, 233, 0.10);
}

/* "Live" badge pulse animation */
@keyframes jf-live-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
}
.jf-live-pulse {
  animation: jf-live-pulse 1.2s ease-in-out infinite;
}

/* ── Simulate button ────────────────────────────────────────── */
.simulate-btn { white-space: nowrap; }

/* ── Progress bar ───────────────────────────────────────────── */
.progress-bar { transition: width 0.25s ease; }

/* ── Speed slider ───────────────────────────────────────────── */
.speed-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 2px;
  background: #e5e5e5;
  outline: none;
  cursor: pointer;
}
.speed-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #1A8DE9;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(26,141,233,0.35);
  transition: transform 0.1s;
}
.speed-slider::-webkit-slider-thumb:hover { transform: scale(1.25); }
.speed-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #1A8DE9;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 4px rgba(26,141,233,0.35);
}

/* ── Datepicker overrides ───────────────────────────────────── */
.datepicker-custom ::v-deep .mx-input {
  border-radius: 8px;
  border-color: #e5e5e5;
  font-size: 12px;
  height: 32px;
  box-shadow: none;
  color: #525252;
}
.datepicker-custom ::v-deep .mx-input:focus {
  border-color: #1A8DE9;
  box-shadow: 0 0 0 2px rgba(26,141,233,0.12);
}
.datepicker-custom ::v-deep .mx-icon-calendar { color: #1A8DE9; }

/* ── Live info floating card ────────────────────────────────── */

/* ── Responsive optimization panel ──────────────────────────── */
@media (max-width: 768px) {
  .optimization-metrics-panel {
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    max-width: none;
  }
}
.live-info-card {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: #ffffff;
  border-radius: 16px;
  padding: 12px 18px;
  display: flex;
  align-items: stretch;
  box-shadow: 0 8px 36px rgba(26,141,233,0.16), 0 2px 10px rgba(0,0,0,0.07);
  border: 1.5px solid rgba(26,141,233,0.16);
  border-left: 4px solid #1A8DE9;
  font-size: 12px;
  line-height: 1.5;
  max-width: 360px;
  min-width: 230px;
  pointer-events: none;
  backdrop-filter: blur(8px);
}
.live-info-text { flex: 1; min-width: 0; }

/* ── Optimized path overlay ─────────────────────────────────── */
.optimized-path {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 9999;
  background: white;
  border-right: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  padding: 10px;
  overflow-y: auto;
  max-height: 60vh;
  width: 100%;
}
@media (min-width: 640px) {
  .optimized-path { width: 22rem; max-height: 70vh; }
}
@media (min-width: 1024px) {
  .optimized-path { max-height: 83vh; }
}

/* ── Skeleton pulse ─────────────────────────────────────────── */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
.animate-pulse { animation: pulse 1.6s cubic-bezier(0.4,0,0.6,1) infinite; }

/* ── Transitions ────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

.stats-fade-enter-active, .stats-fade-leave-active { transition: all 0.3s ease; }
.stats-fade-enter, .stats-fade-leave-to { opacity: 0; transform: translateY(-6px); }

.badge-pop-enter-active, .badge-pop-leave-active { transition: all 0.2s ease; }
.badge-pop-enter, .badge-pop-leave-to { opacity: 0; transform: scale(0.75); }

.info-slide-enter-active, .info-slide-leave-active { transition: all 0.3s ease; }
.info-slide-enter, .info-slide-leave-to { opacity: 0; transform: translateX(-50%) translateY(-10px); }
.info-slide-enter-to, .info-slide-leave-from { opacity: 1; transform: translateX(-50%) translateY(0); }

/* ── text-xxs utility (fallback) ────────────────────────────── */
.text-xxs { font-size: 0.65rem; line-height: 1rem; }

/* ── Safe area (iPhone notch) ───────────────────────────────── */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .left-panel > div:last-child {
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }
}

/* 1. Remove the large header space and move close button to the top-right corner */
::v-deep .gm-style-iw-chr {
  height: 0px !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  z-index: 1;
}

/* 2. Style the default button to be smaller and cleaner */
::v-deep .gm-style-iw-chr button {
  top: -8px !important;
  right: -5px !important;
  width: 45px !important;
  height: 28px !important;
}
::v-deep .gm-style-iw-chr button span {
  width: 18px !important;
  height: 18px !important;
  margin: 15px !important;
}

/* 3. Remove all padding from the main window container for a flush look */
::v-deep .gm-style-iw-c {
  padding: 0 !important;
}

/* 4. Remove internal margins so your content starts at the very top */
::v-deep .gm-style-iw-d {
  margin: 0 !important;
  padding: 5px !important;
  overflow: hidden !important;
}
</style>
